import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CreditCard, Calculator, Clock, Shield, CheckCircle, ArrowRight, DollarSign, Zap, Target } from 'lucide-react';
import SEO, { SEOPages } from '../components/SEO';

const CreditsPrives = () => {
  // État pour le calculateur de crédit privé
  const [creditCalculator, setCreditCalculator] = useState({
    montantCredit: 25000,
    dureeAnnees: 4,
    tauxInteret: 5.95
  });

  const [calculatorResult, setCalculatorResult] = useState(null);

  const creditServices = [
    {
      icon: Zap,
      title: "Réponse express",
      description: "Votre demande traitée dans un délai de 24h",
      benefits: ["Procédure simplifiée", "Réponse immédiate", "Sans paperasse excessive"]
    },
    {
      icon: Shield,
      title: "Conditions transparentes",
      description: "Taux fixes sans frais cachés, dès 5,95%",
      benefits: ["Taux fixe garanti", "Aucun frais de dossier", "Remboursement anticipé possible"]
    },
    {
      icon: Target,
      title: "Solutions sur mesure",
      description: "Crédits adaptés à votre situation et vos projets",
      benefits: ["Montants flexibles", "Durées modulables", "Conditions personnalisées"]
    },
    {
      icon: Clock,
      title: "Accompagnement complet",
      description: "Suivi personnel de votre dossier par nos experts",
      benefits: ["Conseiller dédié", "Suivi personnalisé", "Support continu"]
    }
  ];

  const creditUsages = [
    {
      title: "Rénovation & Travaux",
      description: "Financez vos projets d'amélioration immobilière",
      amount: "CHF 5'000 - 150'000",
      rate: "dès 5.95%"
    },
    {
      title: "Véhicule",
      description: "Achat de voiture, moto ou véhicule utilitaire",
      amount: "CHF 10'000 - 100'000", 
      rate: "dès 5.95%"
    },
    {
      title: "Consolidation de dettes",
      description: "Regroupez vos crédits pour réduire vos mensualités",
      amount: "CHF 15'000 - 250'000",
      rate: "dès 5.95%"
    },
    {
      title: "Projets personnels",
      description: "Mariage, voyage, formation ou tout autre projet",
      amount: "CHF 5'000 - 80'000",
      rate: "dès 5.95%"
    }
  ];

  const calculateCredit = () => {
    const montant = parseFloat(creditCalculator.montantCredit);
    const duree = parseFloat(creditCalculator.dureeAnnees);
    const taux = parseFloat(creditCalculator.tauxInteret) / 100 / 12;
    const nbMois = duree * 12;

    // Calcul mensualité avec formule des annuités
    const mensualite = montant * (taux * Math.pow(1 + taux, nbMois)) / (Math.pow(1 + taux, nbMois) - 1);
    
    // Coût total du crédit
    const coutTotal = mensualite * nbMois;
    const interetsTotal = coutTotal - montant;

    setCalculatorResult({
      mensualite: Math.round(mensualite),
      coutTotal: Math.round(coutTotal),
      interetsTotal: Math.round(interetsTotal)
    });
  };

  const handleCalculatorChange = (field, value) => {
    setCreditCalculator(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <div className="pt-20">
      <SEO {...SEOPages.creditsPrives} />
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          role="img"
          aria-label="Crédit privé suisse - financement projet personnel avec meilleurs taux"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1479292889369-1a48f234247e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxtb2Rlcm58ZW58MHx8fHwxNzU3MzQ3NzczfDA&ixlib=rb-4.1.0&q=85)',
            backgroundBlendMode: 'overlay'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-black/40"></div>
        </div>
        
        <div className="container relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl lg:text-6xl font-bold heading-display text-white mb-6">
              Crédits privés : <span className="text-green-300">réalisez</span> tous vos projets
            </h1>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              <span className="text-green-300 font-semibold">Taux dès 5,95%</span> - Conditions transparentes et décision rapide. 
              Financez vos projets avec la solution crédit la plus avantageuse de Suisse.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Link to="/demande-credit" className="btn-glass btn-primary text-lg px-8 py-4 flex items-center justify-center">
                Demander mon crédit
                <ArrowRight className="ml-2" size={20} />
              </Link>
              <a 
                href="#calculateur"
                className="btn-glass text-lg px-8 py-4 flex items-center justify-center"
              >
                <Calculator className="mr-2" size={20} />
                Calculer mes mensualités
              </a>
            </div>

            {/* Statistiques */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto">
              <div className="glass p-6 rounded-2xl">
                <div className="text-3xl font-bold text-green-400 mb-2">5.95%</div>
                <div className="text-gray-300">Taux crédit dès</div>
              </div>
              <div className="glass p-6 rounded-2xl">
                <div className="text-3xl font-bold text-blue-400 mb-2">24h</div>
                <div className="text-gray-300">Décision rapide</div>
              </div>
              <div className="glass p-6 rounded-2xl">
                <div className="text-3xl font-bold text-white mb-2">CHF 250K</div>
                <div className="text-gray-300">Montant maximum</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Calculateur de crédit privé */}
      <section id="calculateur" className="py-20">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold heading-display text-white mb-6">
              Calculateur de crédit privé
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Calculez instantanément vos mensualités et découvrez nos conditions avantageuses
            </p>
          </div>
          
          <div className="max-w-5xl mx-auto">
            <div className="glass p-8 rounded-3xl">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                {/* Paramètres */}
                <div className="space-y-6">
                  <h3 className="text-2xl font-semibold text-white mb-6">Simulez votre crédit</h3>
                  
                  <div>
                    <label className="block text-sm text-gray-300 mb-2">Montant souhaité (CHF)</label>
                    <div className="relative">
                      <input
                        type="range"
                        min="3000"
                        max="250000"
                        step="1000"
                        value={creditCalculator.montantCredit}
                        onChange={(e) => handleCalculatorChange('montantCredit', e.target.value)}
                        className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider mb-4"
                      />
                      <div className="flex justify-between text-sm text-gray-400 mb-2">
                        <span>CHF 3'000</span>
                        <span>CHF 250'000</span>
                      </div>
                    </div>
                    <div className="glass p-4 rounded-xl text-center">
                      <div className="text-2xl font-bold text-green-400">
                        CHF {parseInt(creditCalculator.montantCredit).toLocaleString()}
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm text-gray-300 mb-2">Durée (années)</label>
                    <div className="relative">
                      <input
                        type="range"
                        min="1"
                        max="7"
                        step="1"
                        value={creditCalculator.dureeAnnees}
                        onChange={(e) => handleCalculatorChange('dureeAnnees', e.target.value)}
                        className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider mb-4"
                      />
                      <div className="flex justify-between text-sm text-gray-400 mb-2">
                        <span>1 an</span>
                        <span>7 ans</span>
                      </div>
                    </div>
                    <div className="glass p-4 rounded-xl text-center">
                      <div className="text-2xl font-bold text-blue-400">
                        {creditCalculator.dureeAnnees} année{creditCalculator.dureeAnnees > 1 ? 's' : ''}
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm text-gray-300 mb-2">Taux d'intérêt annuel (%)</label>
                    <div className="relative">
                      <input
                        type="range"
                        min="5.95"
                        max="9.95"
                        step="0.1"
                        value={creditCalculator.tauxInteret}
                        onChange={(e) => handleCalculatorChange('tauxInteret', e.target.value)}
                        className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider mb-4"
                      />
                      <div className="flex justify-between text-sm text-gray-400 mb-2">
                        <span>5.95%</span>
                        <span>9.95%</span>
                      </div>
                    </div>
                    <div className="glass p-4 rounded-xl text-center">
                      <div className="text-2xl font-bold text-yellow-400">
                        {creditCalculator.tauxInteret}%
                      </div>
                    </div>
                  </div>
                  
                  <button
                    onClick={calculateCredit}
                    className="w-full btn-glass btn-primary text-lg py-4 flex items-center justify-center"
                  >
                    <DollarSign className="mr-2" size={20} />
                    Calculer mon crédit
                  </button>
                </div>
                
                {/* Résultats */}
                <div className="space-y-6">
                  <h3 className="text-2xl font-semibold text-white mb-6">Vos résultats</h3>
                  
                  {calculatorResult ? (
                    <div className="space-y-4">
                      <div className="glass p-6 rounded-2xl border-2 border-green-500/30">
                        <div className="text-sm text-gray-300">Mensualité</div>
                        <div className="text-4xl font-bold text-green-400">
                          CHF {calculatorResult.mensualite.toLocaleString()}
                        </div>
                        <div className="text-sm text-green-300 mt-1">par mois</div>
                      </div>
                      
                      <div className="glass p-6 rounded-2xl">
                        <div className="text-sm text-gray-300">Coût total du crédit</div>
                        <div className="text-2xl font-bold text-white">
                          CHF {calculatorResult.coutTotal.toLocaleString()}
                        </div>
                      </div>
                      
                      <div className="glass p-6 rounded-2xl">
                        <div className="text-sm text-gray-300">Total des intérêts</div>
                        <div className="text-2xl font-bold text-yellow-400">
                          CHF {calculatorResult.interetsTotal.toLocaleString()}
                        </div>
                      </div>
                      
                      <div className="glass p-4 rounded-xl bg-gradient-to-r from-green-500/20 to-blue-500/20">
                        <div className="text-center">
                          <div className="text-lg font-semibold text-white mb-2">
                            Votre crédit ATOM Group SA
                          </div>
                          <div className="text-sm text-gray-300">
                            Conditions transparentes et personnalisées
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="glass p-12 rounded-2xl text-center">
                      <CreditCard size={48} className="text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-300">
                        Utilisez les curseurs pour calculer vos mensualités
                      </p>
                    </div>
                  )}
                  
                  {calculatorResult && (
                    <div className="mt-6 text-center">
                      <Link to="/demande-credit" className="btn-glass btn-primary px-8 py-3 w-full">
                        Faire ma demande de crédit
                      </Link>
                    </div>
                  )}
                  
                  {/* Texte légal */}
                  <div className="mt-8 space-y-4 text-xs text-gray-400">
                    <p>
                      *La définition du taux d'intérêt exact dépend de votre situation personnelle. Complétez la demande en ligne pour obtenir une offre personnalisée.
                    </p>
                    <div className="pt-2 border-t border-gray-700">
                      <p className="font-semibold text-gray-300 mb-1">Remarque légale</p>
                      <p>
                        L'octroi d'un crédit est interdit s'il entraîne le surendettement de la consommatrice ou du consommateur (mention légale selon l'art. 3 LCD).
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Types de crédits */}
      <section className="py-20">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold heading-display text-white mb-6">
              Nos solutions de crédits privés
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Des conditions adaptées à tous vos projets personnels
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {creditUsages.map((usage, index) => (
              <div key={index} className="glass p-8 rounded-3xl group hover:scale-105 transition-transform">
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">{usage.title}</h3>
                    <p className="text-gray-300 text-sm">{usage.description}</p>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-green-400">{usage.rate}</div>
                  </div>
                </div>
                
                <div className="border-t border-gray-700 pt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300 text-sm">Montant</span>
                    <span className="text-white font-semibold">{usage.amount}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold heading-display text-white mb-6">
              Pourquoi choisir ATOM Group SA ?
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              L'excellence du service crédit avec les meilleures conditions du marché
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {creditServices.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <div key={index} className="glass p-8 group">
                  <div className="w-16 h-16 bg-gradient-to-br from-green-400 to-green-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    <IconComponent size={32} className="text-white" />
                  </div>
                  
                  <h3 className="text-xl font-semibold text-white mb-4">{service.title}</h3>
                  <p className="text-gray-300 mb-6">{service.description}</p>
                  
                  <ul className="space-y-2">
                    {service.benefits.map((benefit, i) => (
                      <li key={i} className="flex items-center text-sm text-gray-300">
                        <CheckCircle size={16} className="text-green-400 mr-2 flex-shrink-0" />
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="container">
          <div className="glass p-12 rounded-3xl text-center">
            <h2 className="text-4xl font-bold heading-display text-white mb-6">
              Prêt à concrétiser votre projet ?
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Bénéficiez de nos taux compétitifs dès 5,95% et de notre service rapide. 
              Réponse garantie sous 24h.
            </p>
            <div className="flex justify-center">
              <Link to="/demande-credit" className="btn-glass btn-primary text-lg px-12 py-4">
                Demander mon crédit maintenant
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CreditsPrives;